/**
 * KnowledgeSynapseLoopView — vibe-designer WS-A2 (2026-07-12).
 *
 * SYNAPSE is a LOOP, not a corpus, so its best-fit visualization is a
 * diagram of the loop itself:
 *
 *   Knowledge sources (MIND + CORTEX) → Compile (LLM synthesis)
 *     → Wiki articles → searchable via OBSIDIAN/MIND ↺ back into sources.
 *
 * Driven entirely by the EXISTING store state (`synapseStatus`,
 * `isSynapseCompiling`) — no new backend. Additive: mounts inside
 * KnowledgeSynapseDetail above the count cards; the existing count cards,
 * Compile button and article inventory are untouched.
 *
 * Honest states: engine Unavailable → the whole loop greys out; never
 * compiled → a calm zero article count (not an error); compiling → the
 * Compile stage pulses.
 */
import clsx from 'clsx';
import { Brain, Network, Zap, BookOpen, Search, ArrowRight } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';

function Stage({
  testId,
  label,
  sublabel,
  icon,
  accent,
  compiling,
}: {
  testId: string;
  label: string;
  sublabel?: React.ReactNode;
  icon: React.ReactNode;
  accent?: boolean;
  compiling?: boolean;
}) {
  return (
    <div
      data-testid={testId}
      data-compiling={compiling === undefined ? undefined : String(compiling)}
      className={clsx(
        'flex flex-col items-center justify-center text-center rounded border px-3 py-2 min-w-[92px] flex-1',
        accent
          ? 'border-vibe-svc-synapse/50 bg-vibe-svc-synapse/10'
          : 'border-vibe-border bg-vibe-bg',
        compiling && 'animate-pulse',
      )}
    >
      <div className={clsx('mb-1', accent ? 'text-vibe-svc-synapse' : 'text-vibe-text-secondary')}>
        {icon}
      </div>
      <div className="text-[11px] font-semibold text-vibe-text leading-tight">{label}</div>
      {sublabel && (
        <div className="text-[10px] text-vibe-text-secondary leading-tight mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}

function Arrow() {
  return (
    <div className="flex items-center text-vibe-text-secondary flex-shrink-0" aria-hidden>
      <ArrowRight className="w-4 h-4" />
    </div>
  );
}

export function KnowledgeSynapseLoopView() {
  const status = useKnowledgeStore((s) => s.synapseStatus);
  const isCompiling = useKnowledgeStore((s) => s.isSynapseCompiling);

  const available = status?.available ?? false;
  const articleCount = status?.article_count ?? 0;

  return (
    <section
      data-testid="knowledge-synapse-loop"
      data-available={String(available)}
      aria-label="Knowledge loop diagram"
      className={clsx(
        'rounded border border-vibe-border bg-vibe-bg-secondary p-3 mb-4 transition-opacity',
        !available && 'opacity-50',
      )}
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary mb-3 flex items-center gap-1.5">
        <Zap className="w-3 h-3 text-vibe-svc-synapse" />
        Knowledge loop
      </h2>

      <div className="flex items-stretch gap-2">
        <Stage
          testId="knowledge-synapse-loop-stage-sources"
          label="Sources"
          sublabel="MIND · CORTEX"
          icon={
            <span className="flex items-center gap-1">
              <Brain className="w-4 h-4" />
              <Network className="w-4 h-4" />
            </span>
          }
        />
        <Arrow />
        <Stage
          testId="knowledge-synapse-loop-stage-compile"
          label="Compile"
          sublabel={isCompiling ? 'synthesizing…' : 'LLM synthesis'}
          icon={<Zap className="w-4 h-4" />}
          accent
          compiling={isCompiling}
        />
        <Arrow />
        <Stage
          testId="knowledge-synapse-loop-stage-articles"
          label="Wiki articles"
          sublabel={
            <span data-testid="knowledge-synapse-loop-article-count" className="tabular-nums">
              {articleCount}
            </span>
          }
          icon={<BookOpen className="w-4 h-4" />}
        />
        <Arrow />
        <Stage
          testId="knowledge-synapse-loop-stage-searchable"
          label="Searchable"
          sublabel="OBSIDIAN · MIND"
          icon={<Search className="w-4 h-4" />}
        />
      </div>

      {/* Return leg — the compiled articles feed back into the searchable
          corpus, closing the loop. */}
      <div className="mt-2 flex items-center gap-1.5 text-[10px] text-vibe-text-secondary">
        <span className="text-vibe-svc-synapse">↺</span>
        <span>
          Compiled articles re-enter the corpus — searchable via OBSIDIAN &amp; MIND, feeding the
          next compile.
        </span>
      </div>
    </section>
  );
}

export default KnowledgeSynapseLoopView;
